import { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Paper } from '@shared/ui/common/paper';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useConnections } from '@shared/hooks';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import ConnectionActiveImports from './ConnectionActiveImports';
import { APP_CONFIG } from '../../../../config/app.config';
import { useToastStore } from '../../../../application/store/toastStore';

interface GenericConnectionSettingsProps {
    connectionId: string;
}

/** Minimal settings for connectors that don't have a dedicated settings UI yet. */
const GenericConnectionSettings = ({ connectionId }: GenericConnectionSettingsProps) => {
    const { onClose } = useGenericDrawer();
    const { connections, removeConnection } = useConnections();
    const connection = connections?.find(c => c.id === connectionId);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const addToast = useToastStore(state => state.addToast);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await removeConnection(connectionId);
            addToast('Connection deleted', 'success');
            onClose();
        } catch {
            addToast('Failed to delete connection', 'error');
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Stack
                direction="row"
                position="sticky"
                top={0}
                p={4}
                zIndex={1}
                bgcolor="background.paper"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    {connection && (
                        <Avatar
                            variant="rounded"
                            src={connection.connector.image}
                            alt={connection.connector.type}
                            sx={{ width: 36, height: 36, border: 1, borderColor: 'divider' }}
                            slotProps={{ img: { style: { objectFit: 'contain' } } }}
                        />
                    )}
                    <Typography variant="h6">{connection?.name ?? 'Connection Settings'}</Typography>
                </Stack>
                {onClose && (
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                )}
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 4, pb: 4 }}>
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Active Imports</Typography>
                    </DialogHeader>
                    <ConnectionActiveImports connectionId={connectionId} />
                </Paper>


                <Paper>
                    <DialogHeader>
                        <Typography variant="h6" color="error.main">Danger Zone</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Deleting this connection will remove all associated listings and data. This action cannot be undone.
                            </Typography>
                            <Box>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete Connection
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Confirm Delete</Typography>
                    <IconButton onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 3 }}>
                    <Typography>
                        Are you sure you want to delete <strong>{connection?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GenericConnectionSettings;
